/**
 * useConsent hook
 *
 * google-sign-in/US-004 (T4): mutation hook wrapping consentApi.recordConsent().
 * On success, invalidates the SAME query key usePebbles.ts registers for
 * GET /users/me (['user', 'profile']) so consentPending/consentCurrent
 * refresh and the app unblocks (AC-004.4), and clears the global
 * consent-gate flag (T5's useConsentGateStore) so the ConsentModal unmounts.
 *
 * TO-LL-036: on failure, error is surfaced (not swallowed) via the
 * mutation's `error` — the caller (ConsentModal, T6) keeps the modal open
 * and shows it inline; this hook never auto-dismisses on failure.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { recordConsent } from '@/services/consentApi';
import { useConsentGateStore } from '@/store/consentGate';
import type { AuthError } from '@/types/auth';

export function useConsent() {
  const queryClient = useQueryClient();
  const clearConsentRequired = useConsentGateStore((s) => s.clearConsentRequired);

  const mutation = useMutation({
    mutationFn: recordConsent,
    onSuccess: () => {
      // Same query key usePebbles.ts uses for GET /users/me — a single
      // targeted refetch, not a full queryClient.clear() (perf: avoid a
      // redundant profile refetch storm).
      queryClient.invalidateQueries({ queryKey: ['user', 'profile'] });
      clearConsentRequired();
    },
  });

  return {
    submitConsent: () => mutation.mutate(),
    isSubmitting: mutation.isPending,
    error: mutation.error as AuthError | null,
  };
}
